import { Flex } from 'antd';
import PropTypes from 'prop-types';
import { ReactComponent as TripVideoUnavailable } from '../../../assets/icons/trip-video-unavailable.svg';
import { ReactComponent as TripVideoInprogress } from '../../../assets/icons/trip-video-inprogress.svg';
import { ReactComponent as TripVideoFailed } from '../../../assets/icons/trip-video-error.svg';
import TimelineCard from './TimelineCard';
import { TRIP_VIDEO_STATUS } from '../../../utilities/constants';
import { useRef, useEffect, useState } from 'react';
import '../TripSurveillanceAndAnalysis.css';

const statusIconMap = {
  [TRIP_VIDEO_STATUS.INPROGRESS]: TripVideoInprogress,
  [TRIP_VIDEO_STATUS.ERROR]: TripVideoFailed,
  [TRIP_VIDEO_STATUS.UNAVAILABLE]: TripVideoUnavailable,
};

const TimelineLayout = ({
  onFragmentClick,
  selectedFragmentDetails,
  fragments = [],
}) => {
  const scrollRef = useRef(null);
  const scrollBarRef = useRef(null);
  const thumbWidthRef = useRef(0);
  const dragStateRef = useRef({ isDragging: false, startX: 0, startLeft: 0 });
  const [hasScroll, setHasScroll] = useState(false);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [thumbStyle, setThumbStyle] = useState({ width: 0, left: 0 });

  const updateThumb = () => {
    const el = scrollRef.current;
    const track = scrollBarRef.current;
    if (!el || !track) return;

    const trackWidth = track.clientWidth;
    if (!trackWidth) return;

    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    if (maxScrollLeft <= 0) {
      thumbWidthRef.current = trackWidth;
      setThumbStyle({ width: trackWidth, left: 0 });
      return;
    }

    const ratio = el.clientWidth / el.scrollWidth;
    const thumbWidth = Math.max(trackWidth * ratio, 40);
    const maxThumbLeft = trackWidth - thumbWidth;
    const thumbLeft = (el.scrollLeft / maxScrollLeft) * maxThumbLeft;

    thumbWidthRef.current = thumbWidth;
    setThumbStyle({ width: thumbWidth, left: thumbLeft });
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      setHasScroll(el.scrollWidth > el.clientWidth);
      setScrollWidth(el.scrollWidth);
    }
  }, [fragments]);

  useEffect(() => {
    if (!hasScroll) return;

    const el = scrollRef.current;
    if (!el) return;

    const handleScroll = () => updateThumb();
    el.addEventListener('scroll', handleScroll);
    updateThumb();

    return () => {
      el.removeEventListener('scroll', handleScroll);
    };
  }, [hasScroll, scrollWidth]);

  useEffect(() => {
    const handleResize = () => {
      const el = scrollRef.current;
      if (!el) return;
      setHasScroll(el.scrollWidth > el.clientWidth);
      setScrollWidth(el.scrollWidth);
      updateThumb();
    };

    globalThis.addEventListener('resize', handleResize);
    handleResize();

    return () => globalThis.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!hasScroll) return;

    const handleMouseMove = event => {
      if (!dragStateRef.current.isDragging) return;
      const el = scrollRef.current;
      const track = scrollBarRef.current;
      if (!el || !track) return;

      const trackWidth = track.clientWidth;
      const maxScrollLeft = el.scrollWidth - el.clientWidth;
      const thumbWidth = thumbWidthRef.current || 1;
      const maxThumbLeft = trackWidth - thumbWidth;
      const deltaX = event.clientX - dragStateRef.current.startX;
      const nextThumbLeft = Math.min(
        Math.max(dragStateRef.current.startLeft + deltaX, 0),
        maxThumbLeft
      );

      el.scrollLeft = (nextThumbLeft / maxThumbLeft) * maxScrollLeft;
    };

    const handleMouseUp = () => {
      dragStateRef.current.isDragging = false;
    };

    globalThis.addEventListener('mousemove', handleMouseMove);
    globalThis.addEventListener('mouseup', handleMouseUp);

    return () => {
      globalThis.removeEventListener('mousemove', handleMouseMove);
      globalThis.removeEventListener('mouseup', handleMouseUp);
    };
  }, [hasScroll]);

  const handleThumbMouseDown = event => {
    event.preventDefault();
    dragStateRef.current.isDragging = true;
    dragStateRef.current.startX = event.clientX;
    dragStateRef.current.startLeft = thumbStyle.left;
  };

  const handleTrackMouseDown = event => {
    if (event.target !== scrollBarRef.current) return;
    const el = scrollRef.current;
    const track = scrollBarRef.current;
    if (!el || !track) return;

    const rect = track.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const trackWidth = track.clientWidth;
    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    const thumbWidth = thumbWidthRef.current || 1;
    const maxThumbLeft = trackWidth - thumbWidth;
    const nextThumbLeft = Math.min(
      Math.max(clickX - thumbWidth / 2, 0),
      maxThumbLeft
    );

    el.scrollLeft = (nextThumbLeft / maxThumbLeft) * maxScrollLeft;
  };

  const handleTrackWheel = event => {
    if (!hasScroll) return;
    const el = scrollRef.current;
    if (!el) return;

    let delta = event.deltaY !== 0 ? event.deltaY : event.deltaX;
    if (!delta) return;

    if (event.deltaMode === 1) {
      delta *= 16;
    } else if (event.deltaMode === 2) {
      delta *= window.innerHeight;
    }

    event.preventDefault();
    el.scrollLeft += delta;
  };

  return (
    <div className="timeline-layout-wrapper" onWheel={handleTrackWheel}>
      <Flex
        align="center"
        className="timeline-layout-flex"
        style={{
          height: 90,
        }}
      >
        <div className="timeline-layout-border" />
        <Flex
          ref={scrollRef}
          className="timeline-layout-scroll"
          gap={1}
          onWheel={handleTrackWheel}
        >
          {fragments?.map((fragment, index) => {
            const IconComponent = statusIconMap[fragment?.vod_status];
            const isSOS = fragment?.is_sos_altered || false;
            const thumbnailImage = fragment.thumbnail;
            const timestamp = fragment?.fragment_timestamp
              ? new Date(fragment?.fragment_timestamp).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                })
              : '--:--';
            return (
              <TimelineCard
                onClick={
                  fragment?.vod_status === TRIP_VIDEO_STATUS.AVAILABLE ||
                  fragment?.vod_status === TRIP_VIDEO_STATUS.UNAVAILABLE
                    ? () => onFragmentClick(fragment)
                    : undefined
                }
                IconComponent={IconComponent}
                isSOS={isSOS}
                key={
                  fragment?.fragment_uuid ||
                  fragment?.fragment_sequence ||
                  index
                }
                thumbnailImage={thumbnailImage}
                timestamp={timestamp}
                vodStatus={fragment?.vod_status}
                isSelected={
                  fragment?.fragment_uuid ===
                  selectedFragmentDetails?.fragment_uuid
                }
              />
            );
          })}
        </Flex>
        <div className="timeline-layout-border" />
      </Flex>
      {hasScroll && (
        <div
          ref={scrollBarRef}
          className="timeline-layout-scrollbar"
          onMouseDown={handleTrackMouseDown}
          onWheel={handleTrackWheel}
          role="slider"
          tabIndex={0}
          aria-valuenow={thumbStyle.left}
          aria-valuemin={0}
          aria-valuemax={scrollWidth - thumbStyle.width}
          aria-label="Timeline scrollbar thumb"
        >
          <div
            className="timeline-layout-scrollbar-thumb"
            style={{
              width: `${thumbStyle.width}px`,
              transform: `translateX(${thumbStyle.left}px)`,
            }}
            onMouseDown={handleThumbMouseDown}
            role="presentation"
          />
        </div>
      )}
    </div>
  );
};

TimelineLayout.propTypes = {
  onFragmentClick: PropTypes.func.isRequired,
  selectedFragmentDetails: PropTypes.object,
  fragments: PropTypes.array,
};

export default TimelineLayout;
